/**
 * Dental PPO skeleton
 *
 * Provides the default structure for Dental PPO coverage.
 * Returns grid format (flat fields) for use in the PlanDesigner grid.
 */

import {
  BaseBenefitPlanPolicyPeriod,
  DentalObjectTypeEnum,
  IndividualDeductibleEnum,
  NetworkEnum,
  RfpDentalServiceCategoryEnum,
  RfpDollarBenefitEnum,
  RfpOrthoAgeLimitType,
  RfpPercentageBenefitTypeEnum,
} from "@platform/openapi-types";

import {
  BenefitPlanTypeEnum,
  PlanOptionNameEnum,
} from "../../types/rfp.api.types";

import { DentalPpoBenefitGrid, GridRfpCoverage } from "./types";

/**
 * Creates an empty Dental PPO benefit object in grid format
 */
export const getDentalPpoBenefitSkeleton = (): DentalPpoBenefitGrid => ({
  objectType: DentalObjectTypeEnum.dental,

  networkBenefits: [
    {
      network: NetworkEnum.inNetwork,
      networkName: "",
      individualDeductibleType: IndividualDeductibleEnum.dollarAmount,
      individualDeductibleAmount: undefined,
      familyDeductibleType: RfpDollarBenefitEnum.notIncluded,
      familyDeductibleAmount: undefined,
      annualMaximumType: RfpDollarBenefitEnum.notIncluded,
      annualMaximumAmount: undefined,
      preventativeType1Type: RfpPercentageBenefitTypeEnum.notIncluded,
      preventativeType1Amount: undefined,
      basicType2Type: RfpPercentageBenefitTypeEnum.notIncluded,
      basicType2Amount: undefined,
      majorType3Type: RfpPercentageBenefitTypeEnum.notIncluded,
      majorType3Amount: undefined,
      orthoType4Type: RfpPercentageBenefitTypeEnum.notIncluded,
      orthoType4Amount: undefined,
      orthoMaximumType: RfpDollarBenefitEnum.notIncluded,
      orthoMaximumAmount: undefined,
      orthoAgeLimitType: RfpOrthoAgeLimitType.notIncluded,
      orthoAgeLimitAge: undefined,
      sealants: RfpDentalServiceCategoryEnum.notIncluded,
      spaceMaintainers: RfpDentalServiceCategoryEnum.notIncluded,
      generalAnesthesia: RfpDentalServiceCategoryEnum.notIncluded,
      compositeWhiteFillings: RfpDentalServiceCategoryEnum.notIncluded,
      endodontics: RfpDentalServiceCategoryEnum.notIncluded,
      periodonticsNonSurgical: RfpDentalServiceCategoryEnum.notIncluded,
      periodonticsSurgical: RfpDentalServiceCategoryEnum.notIncluded,
      oralSurgerySimple: RfpDentalServiceCategoryEnum.notIncluded,
      oralSurgeryComplex: RfpDentalServiceCategoryEnum.notIncluded,
      implants: RfpDentalServiceCategoryEnum.notIncluded,
    },
    {
      network: NetworkEnum.outOfNetwork,
      networkName: "",
      individualDeductibleType: IndividualDeductibleEnum.dollarAmount,
      individualDeductibleAmount: undefined,
      familyDeductibleType: RfpDollarBenefitEnum.notIncluded,
      familyDeductibleAmount: undefined,
      annualMaximumType: RfpDollarBenefitEnum.notIncluded,
      annualMaximumAmount: undefined,
      preventativeType1Type: RfpPercentageBenefitTypeEnum.notIncluded,
      preventativeType1Amount: undefined,
      basicType2Type: RfpPercentageBenefitTypeEnum.notIncluded,
      basicType2Amount: undefined,
      majorType3Type: RfpPercentageBenefitTypeEnum.notIncluded,
      majorType3Amount: undefined,
      orthoType4Type: RfpPercentageBenefitTypeEnum.notIncluded,
      orthoType4Amount: undefined,
      orthoMaximumType: RfpDollarBenefitEnum.notIncluded,
      orthoMaximumAmount: undefined,
      orthoAgeLimitType: RfpOrthoAgeLimitType.notIncluded,
      orthoAgeLimitAge: undefined,
      sealants: RfpDentalServiceCategoryEnum.notIncluded,
      spaceMaintainers: RfpDentalServiceCategoryEnum.notIncluded,
      generalAnesthesia: RfpDentalServiceCategoryEnum.notIncluded,
      compositeWhiteFillings: RfpDentalServiceCategoryEnum.notIncluded,
      endodontics: RfpDentalServiceCategoryEnum.notIncluded,
      periodonticsNonSurgical: RfpDentalServiceCategoryEnum.notIncluded,
      periodonticsSurgical: RfpDentalServiceCategoryEnum.notIncluded,
      oralSurgerySimple: RfpDentalServiceCategoryEnum.notIncluded,
      oralSurgeryComplex: RfpDentalServiceCategoryEnum.notIncluded,
      implants: RfpDentalServiceCategoryEnum.notIncluded,
    },
  ],

  // Shared / Benefit Level fields
  deductibleDuration: undefined,
  isDeductibleWaivedPreventative: undefined,
  outOfNetworkReimbursement: undefined,
  maximumRollover: undefined,
  missingToothExclusion: undefined,

  // Administrative
  annualOpenEnrollment: undefined,
  dependentAgeLimitYears: undefined,
  employerContribution: undefined,

  // Service waiting periods
  serviceWaitingPeriods: undefined,
  serviceWaitingPeriodsMonths: undefined,

  // Extended (non-v5) fields — CD-1659/CD-1660
  preventiveAdvantage: undefined,
  oralExams: undefined,
  oralExamFrequency: undefined,
  cleaningFrequency: undefined,
  bitewingXRays: undefined,
  fullMouthXRays: undefined,
  fullMouthXRaysFrequency: undefined,
  fluorideTreatments: undefined,
  amalgamSilverFillings: undefined,
  simpleExtractions: undefined,
  bridgesDentures: undefined,
  bridgesDenturesFrequency: undefined,
  crowns: undefined,
  crownsFrequency: undefined,
  tmj: undefined,
  workInProgressIncluded: undefined,
  openOrClosedContract: undefined,
  eligibilityHours: undefined,
  weightedAvgOutOfPocket: undefined,
});

/**
 * Gets the Dental PPO coverage skeleton
 */
export const getDentalPpoSkeleton = (): Partial<GridRfpCoverage> => ({
  benefitPlans: [
    {
      identifier: "dental-ppo-plan-1",
      name: "Dental PPO Plan",
      numberOfLives: 0,
      type: BenefitPlanTypeEnum.PPO,
      policyPeriod: BaseBenefitPlanPolicyPeriod.newBusiness,
      fundingType: undefined,
      participationRequirements: "",
      rateGuarantee: undefined,
      rateCap: "",
      optionName: PlanOptionNameEnum.NOT_APPLICABLE,
      benefitClassAvailability: [],
      rateSchedule: [],
      benefits: getDentalPpoBenefitSkeleton(),
    },
  ],
});
